import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { residentSelfRegister } from '../lib/api'
import {
  trackLoginPageView, trackFirstTimeStart, trackFirstTimeComplete,
  trackReturningLoginStart, trackReturningLoginComplete,
  trackSelfRegisterStart, trackSelfRegisterComplete
} from '../lib/analytics'
import {
  Lock, Mail, KeyRound, Eye, EyeOff, CheckCircle2, XCircle,
  X, UserPlus, Building2, DoorOpen, Hash, Loader2, Copy, Check, AlertTriangle
} from 'lucide-react'

type LoginMode = 'choose' | 'first-time' | 'returning'

// ─── Self-Registration Modal ─────────────────────────────────────────────

type RegStep = 'form' | 'pin' | 'done'

function RegisterModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<RegStep>('form')
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    building_name: '',
    unit_number: '',
    room_label: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pinCode, setPinCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [showPw, setShowPw] = useState(false)

  const passwordRequirements = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(form.password)
  }
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean)
  const passwordsMatch = form.password === form.confirm_password && form.password.length > 0

  const isFormValid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    isPasswordValid &&
    passwordsMatch &&
    form.building_name.trim() &&
    form.unit_number.trim() &&
    form.room_label.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!isPasswordValid) {
      setError('Password does not meet requirements')
      return
    }
    if (!passwordsMatch) {
      setError('Passwords do not match')
      return
    }
    setSaving(true)
    try {
      const { confirm_password, ...payload } = form
      const res = await residentSelfRegister(payload)
      setPinCode(res.pin_code)
      setStep('pin')
      trackSelfRegisterComplete()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setSaving(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback - select the text
    }
  }

  const handleConfirm = () => {
    setStep('done')
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-primary-50 border-b border-primary-100">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-primary-900">Request an Account</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-primary-100 rounded-lg">
            <X className="w-5 h-5 text-primary-600" />
          </button>
        </div>

        {/* Step 1: Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Fill in your details and create a password. Management will review your request and you'll receive a PIN code for door access.
            </p>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-3.5 h-3.5 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>

            {/* Password Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Lock className="w-3.5 h-3.5 inline mr-1" />
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10"
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password && (
                <div className="mt-1.5 space-y-0.5">
                  {[
                    { ok: passwordRequirements.length, text: '8+ characters' },
                    { ok: passwordRequirements.uppercase, text: 'Uppercase letter' },
                    { ok: passwordRequirements.lowercase, text: 'Lowercase letter' },
                    { ok: passwordRequirements.number, text: 'Number' },
                    { ok: passwordRequirements.special, text: 'Special character' },
                  ].map(r => (
                    <div key={r.text} className={`text-xs flex items-center gap-1 ${r.ok ? 'text-green-600' : 'text-gray-400'}`}>
                      {r.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {r.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Lock className="w-3.5 h-3.5 inline mr-1" />
                Confirm Password
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  form.confirm_password
                    ? passwordsMatch ? 'border-green-400' : 'border-red-400'
                    : 'border-gray-300'
                }`}
                placeholder="Confirm your password"
                required
              />
              {form.confirm_password && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building2 className="w-3.5 h-3.5 inline mr-1" />
                Building
              </label>
              <input
                type="text"
                value={form.building_name}
                onChange={(e) => setForm({ ...form, building_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. Central House"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Hash className="w-3.5 h-3.5 inline mr-1" />
                  Apartment #
                </label>
                <input
                  type="text"
                  value={form.unit_number}
                  onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g. 14"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DoorOpen className="w-3.5 h-3.5 inline mr-1" />
                  Room
                </label>
                <input
                  type="text"
                  value={form.room_label}
                  onChange={(e) => setForm({ ...form, room_label: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g. C"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving || !isFormValid}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </form>
        )}

        {/* Step 2: Show PIN */}
        {step === 'pin' && (
          <div className="p-6 space-y-5">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-3">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Account Created!</h4>
              <p className="text-sm text-gray-600 mt-1">
                Your account and password have been set up. <strong>Save your PIN code below</strong> — you'll need it for door access.
              </p>
            </div>

            {/* PIN Display */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Your PIN Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-mono font-bold text-primary-700 tracking-[0.3em]">
                  {pinCode}
                </span>
                <button
                  onClick={handleCopy}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Copy PIN"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Important:</strong> Write down or screenshot this PIN. It will also be used for door access once your account is approved.
              </div>
            </div>

            {/* Confirm checkbox */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">I have saved my PIN code</span>
            </label>

            <button
              onClick={handleConfirm}
              disabled={!confirmed}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              I've Saved My PIN — Continue
            </button>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 'done' && (
          <div className="p-6 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 rounded-full mb-2">
              <CheckCircle2 className="w-7 h-7 text-primary-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900">You're All Set!</h4>
            <p className="text-sm text-gray-600">
              Management will review your account request. Once approved, come back and use <strong>"Already Registered? Log In"</strong> with your email and the password you just created.
            </p>
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Login Page ─────────────────────────────────────────────────────

export default function Login() {
  const [mode, setMode] = useState<LoginMode>('choose')
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const navigate = useNavigate()
  const { firstLogin: authFirstLogin, login: authLogin } = useAuth()

  // Track login page view on mount
  useEffect(() => { trackLoginPageView() }, [])

  // Password validation for first-time registration
  const passwordRequirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  }
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean)
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0

  const handleFirstTimeLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isPasswordValid) {
      setError('Password does not meet requirements')
      return
    }
    if (!passwordsMatch) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await authFirstLogin(email, pin, newPassword)
      trackFirstTimeComplete()
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const handleReturningLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await authLogin(email, password)
      trackReturningLoginComplete()
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const RequirementIndicator = ({ met, text }: { met: boolean; text: string }) => (
    <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      <span>{text}</span>
    </div>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo/Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome to Ovrwatch</h1>
          <p className="text-gray-600 mt-2">Resident Portal</p>
        </div>

        {/* Mode Chooser */}
        {mode === 'choose' && (
          <div className="space-y-4">
            <button
              onClick={() => { setMode('first-time'); trackFirstTimeStart() }}
              className="w-full p-4 bg-primary-50 border-2 border-primary-200 rounded-xl hover:bg-primary-100 hover:border-primary-300 transition-all text-left"
            >
              <p className="font-semibold text-primary-900 text-lg">First Time? Set Up Your Account</p>
              <p className="text-sm text-primary-700 mt-1">Use your email and either your PIN or phone number on file to create your password</p>
            </button>
            <button
              onClick={() => { setMode('returning'); trackReturningLoginStart() }}
              className="w-full p-4 bg-gray-50 border-2 border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all text-left"
            >
              <p className="font-semibold text-gray-900 text-lg">Already Registered? Log In</p>
              <p className="text-sm text-gray-600 mt-1">Sign in with your email and password</p>
            </button>
          </div>
        )}

        {/* First-Time Registration Form */}
        {mode === 'first-time' && (
          <>
            {/* Steps Guide */}
            <div className="mb-5 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">How it works:</p>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Enter the email address on file for your lease</li>
                <li>Enter either your 5-digit PIN or your phone number on file</li>
                <li>Create a password for your account</li>
              </ol>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleFirstTimeLogin} className="space-y-4">
              <div>
                <label htmlFor="email-first" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email-first"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1">
                  PIN Code or Phone Number
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="pin"
                    type="text"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="5-digit PIN or 10-digit phone"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use your 5-digit door PIN or your 10-digit phone number on file
                </p>
              </div>

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Create Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Confirm password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && (
                  <p className={`text-xs mt-1 ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              {newPassword.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  <p className="text-xs font-medium text-gray-700 mb-1">Password must contain:</p>
                  <RequirementIndicator met={passwordRequirements.length} text="At least 8 characters" />
                  <RequirementIndicator met={passwordRequirements.uppercase} text="One uppercase letter (A-Z)" />
                  <RequirementIndicator met={passwordRequirements.lowercase} text="One lowercase letter (a-z)" />
                  <RequirementIndicator met={passwordRequirements.number} text="One number (0-9)" />
                  <RequirementIndicator met={passwordRequirements.special} text="One special character (!@#$%^&*)" />
                </div>
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  ![5, 10].includes(pin.trim().length) ||
                  !isPasswordValid ||
                  !passwordsMatch
                }
                className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Setting up...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => { setMode('choose'); setError('') }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                ← Back
              </button>
            </div>
          </>
        )}

        {/* Returning User Login Form */}
        {mode === 'returning' && (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleReturningLogin} className="space-y-4">
              <div>
                <label htmlFor="email-return" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email-return"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !password}
                className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => { setMode('choose'); setError('') }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                ← Back
              </button>
            </div>
          </>
        )}

        {/* Help Text - Request Account Link */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Don't have a PIN?{' '}
            <button
              onClick={() => { setShowRegister(true); trackSelfRegisterStart() }}
              className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
            >
              Request an Account
            </button>
          </p>
        </div>

        {/* Property Management Login Link — navigates to admin login on same subdomain */}
        <div className="mt-4 pt-4 border-t border-gray-100 text-center">
          <button
            onClick={() => navigate('/admin/login')}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Property Management Login
          </button>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} />}
    </div>
  )
}
